
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { BadgePercent, Heart, ShoppingCart } from "lucide-react";
import { useCartStore } from "@/store/cart";
import { useToast } from "@/components/ui/use-toast";
import { ProductConfigDialog } from "./shop/ProductConfigDialog";
import { StoreProductCard } from "./shop/StoreProductCard";
import { StoreProductModal } from "./shop/StoreProductModal";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  discountedPrice?: number;
  imageUrl: string | string[];
  category: string;
  availableSizes?: string[];
  availableColors?: string[];
  printOptions?: string[];
}

interface StoreProduct {
  id: string;
  title: string;
  description: string;
  price: number;
  discount_price?: number;
  images: string[];
  design_image_url?: string;
  store_name: string;
  category: string;
  available_colors: string[];
  available_sizes: string[];
  print_options: string[];
  mockup_images: Record<string, Record<string, string>>;
}

// Sample data with extended properties
const sampleProducts: Record<string, Product[]> = {
  tshirts: [
    {
      id: "1",
      title: "Anime Heroes T-Shirt",
      description: "100% cotton t-shirt featuring popular anime characters",
      price: 29.99,
      discountedPrice: 24.99,
      imageUrl: [
        "/lovable-uploads/4950ff22-9907-430d-82b7-dcf8964491a1.png",
        "/lovable-uploads/e64f6a4e-aa32-4105-976c-1d6d71e41453.png",
        "/lovable-uploads/a27999f7-bc34-4b87-b0df-488c92ddbf7a.png"
      ],
      category: "tshirts",
      availableSizes: ["P", "M", "G", "GG"],
      availableColors: ["Preto", "Branco", "Cinza"],
      printOptions: ["Frente", "Costas", "Ambos"]
    },
    {
      id: "2",
      title: "Manga Villains T-Shirt",
      description: "Black t-shirt with famous manga villains design",
      price: 32.99,
      imageUrl: [
        "/lovable-uploads/e64f6a4e-aa32-4105-976c-1d6d71e41453.png",
        "/lovable-uploads/4950ff22-9907-430d-82b7-dcf8964491a1.png"
      ],
      category: "tshirts",
      availableSizes: ["P", "M", "G"],
      availableColors: ["Preto", "Cinza"],
      printOptions: ["Frente", "Ambos"]
    },
    {
      id: "3",
      title: "Logo T-Shirt",
      description: "Minimalist t-shirt with small logo print",
      price: 24.99,
      imageUrl: [
        "/lovable-uploads/a27999f7-bc34-4b87-b0df-488c92ddbf7a.png",
        "/lovable-uploads/4950ff22-9907-430d-82b7-dcf8964491a1.png"
      ],
      category: "tshirts",
      availableSizes: ["P", "M", "G", "GG", "XG"],
      availableColors: ["Preto", "Branco", "Azul"],
      printOptions: ["Frente", "Costas"]
    }
  ],
  hoodies: [
    {
      id: "4",
      title: "Anime Academy Hoodie",
      description: "Warm hoodie with anime academy emblem",
      price: 49.99,
      discountedPrice: 39.99,
      imageUrl: [
        "/lovable-uploads/58fb91ea-8e3f-4b08-a768-08c041caa5d5.png",
        "/lovable-uploads/b146e022-1fe8-40a4-861a-db11d4d1bc90.png"
      ],
      category: "hoodies",
      availableSizes: ["M", "G", "GG"],
      availableColors: ["Preto", "Azul", "Cinza"],
      printOptions: ["Frente", "Costas", "Ambos"]
    },
    {
      id: "5",
      title: "Manga Artist Hoodie",
      description: "Comfortable hoodie for aspiring artists",
      price: 54.99,
      imageUrl: [
        "/lovable-uploads/b146e022-1fe8-40a4-861a-db11d4d1bc90.png",
        "/lovable-uploads/58fb91ea-8e3f-4b08-a768-08c041caa5d5.png"
      ],
      category: "hoodies",
      availableSizes: ["P", "M", "G", "GG"],
      availableColors: ["Preto", "Branco", "Vermelho"],
      printOptions: ["Frente", "Ambos"]
    }
  ],
  misc: [
    {
      id: "6",
      title: "Mochila Anime Edition",
      description: "Mochila espaçosa com estampa de anime",
      price: 79.99,
      imageUrl: [
        "/lovable-uploads/c577f9da-620e-45e7-8bb9-1c7314b7862e.png",
        "/lovable-uploads/42df6479-9827-4762-825f-571963bd0ca4.png"
      ],
      category: "misc",
      availableColors: ["Preto", "Azul", "Vermelho"]
    },
    {
      id: "7",
      title: "Chapéu Otaku Style",
      description: "Chapéu estiloso para fãs de anime",
      price: 34.99,
      imageUrl: "/lovable-uploads/42df6479-9827-4762-825f-571963bd0ca4.png",
      category: "misc"
    },
    {
      id: "8",
      title: "Chaveiro Personagem",
      description: "Chaveiro de personagem popular de anime",
      price: 14.99,
      discountedPrice: 9.99,
      imageUrl: "/lovable-uploads/c577f9da-620e-45e7-8bb9-1c7314b7862e.png",
      category: "misc"
    },
    {
      id: "9",
      title: "Carteira Anime",
      description: "Carteira com design inspirado em anime",
      price: 29.99,
      imageUrl: "/lovable-uploads/42df6479-9827-4762-825f-571963bd0ca4.png",
      category: "misc"
    },
    {
      id: "10",
      title: "Caderno Mangá",
      description: "Caderno com capa ilustrada por artistas de mangá",
      price: 19.99,
      imageUrl: "/lovable-uploads/c577f9da-620e-45e7-8bb9-1c7314b7862e.png",
      category: "misc"
    },
    {
      id: "11",
      title: "Bottom Colecionável",
      description: "Conjunto de bottoms colecionáveis de anime",
      price: 8.99,
      imageUrl: "/lovable-uploads/42df6479-9827-4762-825f-571963bd0ca4.png",
      category: "misc"
    }
  ]
};

type ProductGridProps = {
  category: keyof typeof sampleProducts;
};

export const ProductGrid = ({ category }: ProductGridProps) => {
  const { toast } = useToast();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedStoreProduct, setSelectedStoreProduct] = useState<StoreProduct | null>(null);

  // Fetch store products from database
  const { data: storeProducts } = useQuery({
    queryKey: ['store-products', category],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('store_products')
        .select('*')
        .eq('category', category)
        .eq('is_active', true);

      if (error) {
        console.error('Error fetching store products:', error);
        return [];
      }

      return data as StoreProduct[];
    },
  });

  // Use React Query for sample data
  const { data: products } = useQuery({
    queryKey: ['products', category],
    queryFn: async () => {
      console.log("Fetching products for category:", category);
      return sampleProducts[category] || [];
    },
  });

  const handleOpenProductConfig = (product: Product) => {
    setSelectedProduct(product);
  };

  const handleCloseProductConfig = () => {
    setSelectedProduct(null);
  };

  const handleOpenStoreProductModal = (product: StoreProduct) => {
    setSelectedStoreProduct(product);
  };

  const handleCloseStoreProductModal = () => {
    setSelectedStoreProduct(null);
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {/* Store Products (from database) */}
        {storeProducts?.map((product) => (
          <StoreProductCard
            key={`store-${product.id}`}
            product={product}
            onProductClick={handleOpenStoreProductModal}
          />
        ))}

        {/* Sample Products */}
        {products?.map((product) => (
          <Card key={product.id} className="overflow-hidden group">
            <div 
              className="aspect-square relative overflow-hidden cursor-pointer"
              onClick={() => handleOpenProductConfig(product)}
            >
              <img 
                src={Array.isArray(product.imageUrl) ? product.imageUrl[0] : product.imageUrl} 
                alt={product.title} 
                className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
              />
              {product.discountedPrice && (
                <div className="absolute top-2 right-2 bg-primary text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                  <BadgePercent className="h-3 w-3" />
                  <span>
                    {Math.round(((product.price - product.discountedPrice) / product.price) * 100)}%
                  </span>
                </div>
              )}
              <Button 
                variant="ghost" 
                size="icon" 
                className="absolute top-2 left-2 bg-black/20 text-white hover:bg-black/40 hover:text-white rounded-full"
              >
                <Heart className="h-4 w-4" />
              </Button>
            </div>
            <CardHeader className="p-4 pb-0">
              <CardTitle className="text-lg">{product.title}</CardTitle>
              <CardDescription className="line-clamp-2 text-xs mt-1">
                {product.description}
              </CardDescription>
            </CardHeader>
            <CardFooter className="p-4 pt-2 flex justify-between items-center">
              <div>
                {product.discountedPrice ? (
                  <div className="flex gap-2 items-baseline">
                    <span className="font-bold">R$ {product.discountedPrice.toFixed(2)}</span>
                    <span className="text-muted-foreground line-through text-xs">R$ {product.price.toFixed(2)}</span>
                  </div>
                ) : (
                  <span className="font-bold">R$ {product.price.toFixed(2)}</span>
                )}
              </div>
              <Button 
                size="sm" 
                className="rounded-full"
                onClick={() => handleOpenProductConfig(product)}
              >
                <ShoppingCart className="h-4 w-4 mr-1" />
                Adicionar
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>

      {selectedProduct && (
        <ProductConfigDialog
          isOpen={!!selectedProduct}
          onClose={handleCloseProductConfig}
          product={selectedProduct}
        />
      )}

      <StoreProductModal
        product={selectedStoreProduct}
        isOpen={!!selectedStoreProduct}
        onClose={handleCloseStoreProductModal}
      />
    </>
  );
};
